<template>
  <div class="shop">
    <div
      v-if="featuredArtwork"
      class="shop__background"
      :style="{
        backgroundImage: `url(${featuredArtwork.header_image})`,
      }"
    ></div>

    <div class="shop__content">
      <div v-if="featuredArtwork" class="shop__details-box">
        <span class="shop__details-box_subtitle">FEATURED PIECE</span>
        <h2 class="shop__details-box_title">{{ featuredArtwork.title }}</h2>
        <span v-if="featuredArtwork.artist" class="shop__details-box_artist">
          {{ featuredArtwork.artist.first_name }} {{ featuredArtwork.artist.last_name }}
        </span>
        <div
          class="shop__details-box_details collect-editor collect-editor--white"
          v-html="featuredArtwork.description"
        ></div>
        <BaseButton
          class="shop__details-box_button button--xs button--white"
          text="VIEW"
          @click="toArtworks(`/artworks/${featuredArtwork.slug}`)"
        />
      </div>
      <ShopTitleBox
        :sortData="sortData"
        :selectedSort="cardData.sort"
        :filters="filters"
        :search="search"
        @sort="selectSort"
        @open-filters="openFilters = $event"
        @select-filter="selectFilter"
        @apply-filters="refreshSelectedFilters"
        @clear-filters="refreshSelectedFilters(true)"
        @change-search="changeSearch"
      />
      <ShopList
        v-if="shopList.length"
        :shopList="shopList"
        :cardData="cardData"
        :setStatePage="setStatePage"
      />
      <h6 v-else class="shop__empty">No artworks</h6>
      <div v-if="showLoadMoreButton && hasMorePages" class="shop__button-box">
        <BaseButton
          class="shop__button-box_button button--xs button--white"
          text="LOAD MORE"
          @click.native="handleLoadMoreClick"
        />
      </div>
    </div>
    <div ref="infiniteTrigger" style="height: 1px"></div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import BaseButton from "@/components/buttons/BaseButton.vue";
import useInfiniteScroll from "@/composables/useInfiniteScroll.js";
import ShopTitleBox from "@/views/public/shop/components/ShopTitleBox.vue";
import ShopList from "@/views/public/shop/components/ShopList.vue";
import useSeoHead from "@/composables/useSeoHead";

export default {
  components: { BaseButton, ShopTitleBox, ShopList },

  data() {
    return {
      sortData: [
        { id: "newest_to_oldest", title: "Newest to Oldest" },
        { id: "oldest_to_newest", title: "Oldest to Newest" },
        { id: "artist_a_z", title: "Artist (A-Z)" },
        { id: "artist_z_a", title: "Artist (Z-A)" },
        { id: "title_a_z", title: "Art (A-Z)" },
        { id: "title_z_a", title: "Art (Z-A)" },
      ],
      cardData: {
        page: 1,
        sort: "newest_to_oldest",
        art_types: "",
        materials: "",
        price_start: "",
        price_end: "",
        periods: "",
        colors: "",
        regions: "",
        availabilities: "",
        width_start: "",
        width_end: "",
        height_start: "",
        height_end: "",
        depth_start: "",
        depth_end: "",
      },
      filterBlock: {
        list: "art_types,materials,price,periods,colors,regions,availabilities,sizes",
        type: "collection",
      },
      openFilters: false,
      showLoadMoreButton: true,
      loading: false,
    };
  },
  head() {
    const image = this.featuredArtwork?.header_image ?? "/img/icons/favicon-500x500.png";

    return useSeoHead({
      title: "Artworks browngrotta arts",
      description: "Description Artworks browngrotta arts",
      image,
      routePath: this.$route.path,
    });
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (from.path.startsWith(to.path)) {
        if (vm.statePage.page) {
          vm.cardData.page = vm.statePage.page;
        }
      } else {
        vm.setStatePage({ page: null, scroll: null });
      }
    });
  },
  async created() {
    if (this.statePage.page === null) {
      await this.apiGetShopList(this.cardData);
      this.$emit("updateHead");
      await this.apiGetFiltersBlock({ ...this.filterBlock, search: this.search, isFirst: true });
    }

    const breadcrumbs = [
      {
        title: "Home",
        to: "/",
      },
      {
        title: "Artworks",
        to: "/artworks",
        clickable: false,
      },
    ];
    this.setBreadcrumbs(breadcrumbs);
  },

  computed: {
    ...mapGetters("publicapi/shop", {
      shopList: "getShopList",
      featuredArtwork: "getFeaturedArtwork",
      hasMorePages: "getHasMorePages",
      statePage: "getStatePage",
    }),
    ...mapGetters("publicapi/filters", {
      initialfilters: "getInitialfilters",
      filters: "getFilters",
      search: "getSearch",
      selectedFilters: "getSelectedFilters",
    }),
  },

  methods: {
    ...mapActions("publicapi/shop", ["apiGetShopList"]),
    ...mapActions("publicapi/filters", ["apiGetFiltersBlock", "clear"]),
    ...mapMutations("publicapi/filters", [
      "updateFilters",
      "updateSelectedFilters",
      "removeFromSelectedFilters",
    ]),
    ...mapMutations(["setBreadcrumbs"]),
    ...mapMutations("publicapi/shop", ["setStatePage"]),

    async loadMore() {
      if (this.loading || !this.hasMorePages) return;
      this.loading = true;

      let data = { ...this.cardData, page: this.cardData.page + 1 };
      let success = await this.apiGetShopList(data);
      if (success) {
        this.cardData = data;
      }
      this.loading = false;
    },

    handleLoadMoreClick() {
      this.showLoadMoreButton = false;
      this.loadMore();
      this.infiniteScroll.enableInfiniteScroll();
    },

    async selectSort({ id }) {
      let data = { ...this.cardData, page: 1, sort: id };
      let success = await this.apiGetShopList(data);
      if (success) {
        this.cardData = { ...data };
      }
    },
    selectFilter(val) {
      if (val.apply) {
        this.removeFromSelectedFilters(val);
      }
      this.updateFilters(val);
      if (val.apply) {
        this.refreshSelectedFilters();
      }
    },
    async refreshSelectedFilters(clear) {
      if (clear) {
        await this.clear(this.initialfilters);
      }
      await this.updateSelectedFilters();
      this.filterTable();
    },
    async filterTable() {
      let data = { ...this.cardData, page: 1 };

      this.selectedFilters.forEach(el => {
        const values = ["price", "width", "height", "depth"];
        if (values.includes(el.key)) {
          data[`${el.key}_start`] = "";
          data[`${el.key}_end`] = "";
          el.items.forEach(i => {
            data[i.key] = i.val;
          });
        } else {
          let filters = el.items.map(i => {
            return i.id;
          });
          data[el.key] = filters.join(",");
        }
      });
      let success = await this.apiGetShopList(data);
      if (success) {
        this.cardData = data;
      }
    },
    async changeSearch(search) {
      await this.apiGetFiltersBlock({ ...this.filterBlock, search });
    },
    toArtworks(path) {
      this.setStatePage({ page: this.cardData.page, scroll: window.scrollY });
      this.$router.push(path);
    },
  },
  mounted() {
    this.infiniteScroll = useInfiniteScroll(this);
    if (!this.showLoadMoreButton) {
      this.infiniteScroll.enableInfiniteScroll();
    }
  },
  beforeDestroy() {
    this.infiniteScroll.disableInfiniteScroll();
  },
};
</script>

<style lang="scss" scoped>
* {
  color: $white;
  font-size: 18px;
  line-height: 1.33;
  @media screen and (max-width: $xs) {
    font-size: 16px;
    line-height: 1.56;
  }
}

.shop {
  display: flex;

  &__background {
    @include backgroundBlock();
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 40rem 10.5rem 24rem;
    @media screen and (max-width: $xs) {
      padding: 24.5rem 2rem 11rem;
    }
  }

  &__details-box {
    display: flex;
    flex-direction: column;
    width: 55%;
    @media screen and (max-width: $md) {
      width: 100%;
    }

    &_subtitle {
      font-size: 16px;
      line-height: 1.56;
      @media screen and (max-width: $xs) {
        font-size: 14px;
      }
    }

    &_title {
      font-size: 40px;
      line-height: 1.25;
      @media screen and (max-width: $xs) {
        font-size: 35px;
        margin-bottom: 10px;
      }
    }

    &_artist {
      font-size: 24px;
      line-height: 2;
      @media screen and (max-width: $xs) {
        font-size: 20px;
        line-height: 1.25;
        margin-bottom: 17px;
      }
    }

    &_details {
    }

    &_button {
      margin-top: 24px;
      min-width: 180px;
      @media screen and (max-width: $xs) {
        margin-top: 35px;
      }
    }
  }

  &__empty {
    @include emptyMessage();
  }

  &__button-box {
    @include centeredButtonBox();
  }
}
</style>

